import { IPartialTheme } from "@fluentui/react/lib/Styling"
import { merge } from "lodash"

import { ConfigurationService } from "@encoway/c-services-js-client"
import { L10n } from "@encoway/l10n"
import * as reactConfiguratorImport from "@encoway/react-configurator"

import { ICUIAppState } from "./components/CUIApp/CUIApp.types"
import { IConfigurationSettings } from "./configurationService.types"
import { TRANSLATION_SCOPE } from "./constants"
import { defaultSettings } from "./defaultSettings"
import { IVisualizationSettings } from "./services/visualization/visualizationService.types"

const SESSIONID_REGEXP = /jsessionid=([^?&#])+/g

export interface ITranslations {
    [key: string]: unknown
}

export interface IOAuth2Settings {
    applicationId?: string
    encowayClientSession?: string
    loginUrlPostFix?: string
    oath2ServerAuthenticationUrl?: string
    retrieveTokenUrlPostFix?: string
    retrieveSessionTokenUrlPostFix?: string
    refreshUrlPostFix?: string
    clientSecret?: string
    realm?: string
    redirectUri?: string
}

export interface IAppSettings {
    locale: string
    oauth2?: IOAuth2Settings
    theme?: IPartialTheme
    configuration: IConfigurationSettings
    visualization?: IVisualizationSettings
    translations?: ITranslations
    componentAliases?: object
}

export interface IAppState {
    initialized?: boolean
    error?: any
    translations?: ITranslations
    settings: IAppSettings
    configurationService: ConfigurationService
    reactConfigurator?: typeof reactConfiguratorImport
}

export const hasSessionId = () => {
    const sessionIdAssignments = window.location.href.match(SESSIONID_REGEXP)
    return sessionIdAssignments && sessionIdAssignments.length > 0
}

export const extractJSessionId = (url: string) => {
    const sessionIdAssignments = url.match(SESSIONID_REGEXP)
    if (sessionIdAssignments && sessionIdAssignments.length > 0) {
        const sessionIdAssignment = sessionIdAssignments[0]
        const index = sessionIdAssignment.indexOf("=")
        if (index >= 0) {
            return sessionIdAssignment.substring(index + 1)
        }
    }
}

export const registerAliases = (appState: IAppState) => {
    const aliases = appState.settings?.componentAliases
    if (aliases && appState.reactConfigurator?.ComponentFactory) {
        const componentFactory = appState.reactConfigurator.ComponentFactory
        for (const [alias, componentKey] of Object.entries(aliases)) {
            componentFactory?.addAlias(alias, componentKey)
        }
    }
}

const loadSettingsFile = (): Promise<IAppSettings> => {
    console.log("Loading settings.json...")
    return fetch("settings.json").then((data) => data.json())
}

export const getSettings = async (
    providedSettings?: Promise<IAppSettings>,
    translations?: object
) => {
    const _settings = await (providedSettings || loadSettingsFile())
    return translations
        ? merge({ translations } as IAppSettings, _settings)
        : _settings
}

const ensureJSessionId = (mergedSettings: IAppSettings) => {
    if (!mergedSettings.configuration.jsessionid) {
        mergedSettings.configuration.jsessionid = extractJSessionId(
            window.location.href
        )
    }
}
const mergeConfigurationOptions = (
    mergedSettings: IAppSettings,
    urlParameters: URLSearchParams
) => {
    if (!mergedSettings.configuration.options) {
        mergedSettings.configuration.options = {}
    }
    mergedSettings.configuration.options.configurationId =
        urlParameters.get("configurationId") ||
        mergedSettings.configuration.options.configurationId

    mergedSettings.configuration.options.articleName =
        urlParameters.get("product") ||
        mergedSettings.configuration.options.articleName
    if (!mergedSettings.configuration.options.readOnly) {
        mergedSettings.configuration.options.readOnly =
            urlParameters.get("readOnly") === "true"
    }
}

const mergeVisualizationSettings = (
    visualization: IVisualizationSettings,
    urlParameters: URLSearchParams
) => {
    const vMode = urlParameters.get("visualizationMode")
    if (vMode) {
        visualization.mode = vMode
    }
    if (visualization.mode === "assembly") {
        visualization.settings = merge(
            visualization.settingsAssembly || {},
            visualization.settings
        )
        visualization.settings = {
            ...visualization.settings,
            assembly: {
                ...visualization.settings.assembly,
                catalogRootGroup:
                    urlParameters.get("catalogGroup") ||
                    visualization.settings.assembly?.catalogRootGroup,
                lineItemId:
                    urlParameters.get("lineItemId") ||
                    visualization.settings.assembly?.lineItemId,
                sdmiId:
                    urlParameters.get("sdmiId") ||
                    visualization.settings.assembly?.sdmiId
            }
        }
    } else {
        visualization.settings = merge(
            visualization.settingsConfiguration || {},
            visualization.settings
        )
    }
}

/**
 * Merges custom settings with the applications default settings.
 * @param {Object} appSettings The custom settings to merge.
 */
export async function mergeDefaultSettings(appSettings: IAppState) {
    const { settings, ...args } = appSettings
    // TODO: (FL) Schema validation with ajv to prevent security risks
    let mergedSettings = merge({}, defaultSettings)
    mergedSettings = merge(mergedSettings, settings)

    const urlParameters = new URLSearchParams(window.location.search)

    mergedSettings.locale = urlParameters.get("locale") || mergedSettings.locale
    ensureJSessionId(mergedSettings)
    mergedSettings.configuration.baseUrl =
        urlParameters.get("baseUrl") || mergedSettings.configuration.baseUrl
    mergeConfigurationOptions(mergedSettings, urlParameters)
    const visualization = mergedSettings.visualization
    if (visualization) {
        mergeVisualizationSettings(visualization, urlParameters)
    }
    const pricingDisplayType = urlParameters.get("pricingDisplayType")
    if (pricingDisplayType) {
        mergedSettings.configuration.settings.mappingOptions.pricingDisplayType =
            pricingDisplayType
    }
    return { settings: mergedSettings, ...args } as ICUIAppState
}

export async function loadTranslations(appState: IAppState) {
    if (!appState.settings.translations) {
        return appState
    }

    // Note: Workaround! replace xx_XX locales to be xx-XX locales because encoway l10n can not handle underscores.
    const modifiedLocale = appState.settings.locale.replace("_", "-")

    const translations: ITranslations = appState.settings.translations

    L10n.init("en-US") // set the default locale to en_US
    L10n.source(TRANSLATION_SCOPE, translations, true)
    L10n.currentLocale(modifiedLocale)
    document.documentElement.lang = L10n.currentFullLocale()

    await L10n.reloadResources(L10n.currentFullLocale())
    const modifiedAppState: IAppState = {
        ...appState,
        translations
    }
    modifiedAppState.settings.locale = modifiedLocale
    return modifiedAppState
}
